import { Box, Button, ButtonProps, Stack, Tooltip, Typography } from "@mui/material";
import { StatCard } from "components/StatCard";
import { StatusChip } from "components/StatusChip";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  InvoiceAuditIssueFragment,
  InvoiceAuditSummaryFragment,
  WasteInvoiceChargeAuditResult,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { StatCardGrid } from "../../components/StatCardGrid";
import {
  InvoiceTrackingSummaryCard,
  InvoiceTrackingSummarySkeleton,
} from "../InvoiceTrackingSummaryCard";
import { AUDIT_RESULT_LABELS, formatIssueLabel, formatAuditMoney } from "./formatAuditValues";

export function UnavailableButton({ children, ...props }: ButtonProps) {
  return (
    <Tooltip title="Not available yet">
      <span>
        <Button {...props} disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

export function AuditResult({ result }: { result: WasteInvoiceChargeAuditResult }) {
  return (
    <StatusChip
      label={AUDIT_RESULT_LABELS[result]}
      title={AUDIT_RESULT_LABELS[result]}
      color={result === "RATE_MISMATCH" ? "error" : result === "MATCH" ? "success" : "warning"}
      sx={{ maxWidth: "100%" }}
    />
  );
}

export function AuditFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {children ?? "—"}
      </Typography>
    </Box>
  );
}

export function AuditSummary({
  summary,
  detail = false,
}: {
  summary?: InvoiceAuditSummaryFragment;
  detail?: boolean;
}) {
  if (!summary) return <InvoiceTrackingSummarySkeleton count={4} />;
  const overages = formatAuditMoney(
    "variance",
    summary.calculatedLineCount > 0 ? summary.totalOverages : null
  );
  // The same rule the adherence report's totals follow: a figure is coloured
  // only where it is a finding against the vendor. Money billed and lines
  // counted are just the size of the period; an overage and a line billed off
  // the contracted rate are things someone has to answer for.
  const finding = (count: number) => (count > 0 ? "warning.dark" : "text.primary");
  // Follows what the card actually shows: with nothing calculated the value is
  // a dash, and a dash is not a finding.
  const overageAmount =
    summary.calculatedLineCount > 0 ? (safeParseFloat(String(summary.totalOverages ?? 0)) ?? 0) : 0;
  const metrics = detail
    ? [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
          unit: "Invoiced",
        },
        {
          label: "Total overages",
          value: overages,
          unit: "Over contract",
          color: finding(overageAmount),
        },
        { label: "Line items", value: summary.lineCount.toLocaleString(), unit: "Lines" },
        {
          label: "Rate mismatch lines",
          value: summary.nonCompliantLineCount.toLocaleString(),
          unit: "Lines",
          color: finding(summary.nonCompliantLineCount),
        },
      ]
    : [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
          unit: "Invoiced",
        },
        {
          label: "Total overages",
          value: overages,
          unit: "Over contract",
          color: finding(overageAmount),
        },
        { label: "Total invoice lines", value: summary.lineCount.toLocaleString(), unit: "Lines" },
        {
          label: "Total rate mismatch lines",
          value: summary.nonCompliantLineCount.toLocaleString(),
          unit: "Lines",
          color: finding(summary.nonCompliantLineCount),
        },
      ];
  return (
    <InvoiceTrackingSummaryCard>
      <StatCardGrid testId="audit-summary-cards">
        {metrics.map(({ label, value, unit, color }) => (
          <StatCard
            key={label}
            caption={label}
            value={value}
            subCaption={unit}
            valueColor={color}
            captionPlacement="top"
          />
        ))}
      </StatCardGrid>
    </InvoiceTrackingSummaryCard>
  );
}

export function AuditIssues({ issues }: { issues: readonly InvoiceAuditIssueFragment[] }) {
  return (
    <Stack spacing={1}>
      {issues.map((issue) => (
        <Box key={issue.key}>
          <Typography variant="body2" fontWeight={600}>
            {formatIssueLabel(issue.code)}
          </Typography>
          <Typography variant="body2">{issue.message}</Typography>
          <Typography variant="caption" color="text.secondary">
            {issue.blocksRateCalculation
              ? "Prevents rate calculation"
              : "Does not prevent a complete pre-fee calculation"}
          </Typography>
          {issue.rateCardEntryIds.length > 0 && (
            <Typography variant="caption" display="block" sx={{ overflowWrap: "anywhere" }}>
              Contract rate references: {issue.rateCardEntryIds.join(", ")}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
